import {
    Box,
    Checkbox,
    FormControl,
    FormHelperText,
    FormControlLabel,
    TextField,
    Typography,
    Autocomplete,
    Select,
    MenuItem,
} from '@mui/material';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ErrorIcon from '@mui/icons-material/Error';

import { getAsPhoneNumber } from 'src/utils/get-as-phone-number';
import { defaultOption } from 'src/features/details-form/constants';

import { PhoneInput } from '../phone-input';
import { DateOfBirthInput } from '../date-of-birth-input';
import { PasswordField } from '../password-field';

import { type ControlledFieldProps, FieldTypes, TextFieldTypes } from './types';
import { styles } from './styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ControlledField: FC<ControlledFieldProps<any>> = ({
    name,
    control,
    label,
    placeholder,
    type,
    restStyles,
    options,
    disabled = false,
    fieldType = FieldTypes.Input,
    required = true,
    fullWidth = true,
    id,
    icon,
    isLoginVariant = false,
}) => {
    const { t } = useTranslation();
    const { register } = useFormContext();

    const getFirstOption = (value: string) => value || defaultOption.value;

    let labelComponent: React.ReactNode = (
        <Box css={styles.label}>
            <Typography variant="subtitle4" css={disabled && styles.labelDisabled}>
                {label}
            </Typography>
            {required && !(fieldType === FieldTypes.Input || fieldType === FieldTypes.Password) && (
                <Typography variant="body2" css={styles.asterisk}>
                    *
                </Typography>
            )}
        </Box>
    );

    if (!isLoginVariant && (fieldType === FieldTypes.Input || fieldType === FieldTypes.Password)) {
        labelComponent = (
            <Typography variant="subtitle4" css={disabled && styles.labelDisabled}>
                {label}
            </Typography>
        );
    }

    const endAdornment = icon ? (
        <InputAdornment position="end">
            <IconButton size="small" onClick={icon.onClick}>
                {icon.icon}
            </IconButton>
        </InputAdornment>
    ) : undefined;

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => {
                const error = !!fieldState.error;
                const helperText = t(fieldState.error?.message ?? '');

                const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
                    const { value } = event.target;

                    switch (type) {
                        case TextFieldTypes.EMAIL:
                            field.onChange(value.toLowerCase());
                            break;
                        case TextFieldTypes.POSTCODE:
                            field.onChange(value.toUpperCase());
                            break;
                        default:
                            field.onChange(value);
                    }

                    field.onBlur();
                };

                const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event.target.value);
                };

                switch (fieldType) {
                    case FieldTypes.Select:
                        return (
                            <FormControl css={styles.selectWrapper}>
                                {labelComponent}
                                <Select
                                    {...register(name)}
                                    labelId={name}
                                    {...field}
                                    variant="standard"
                                    fullWidth={fullWidth}
                                    required={required}
                                    error={error}
                                    disabled={disabled}
                                    value={getFirstOption(field.value)}
                                    css={styles.selectText}
                                    id={id}
                                >
                                    {options?.map((option) => {
                                        const isObject = typeof option === 'object';
                                        const optionValue = isObject ? option.value : option;
                                        const optionLabel = isObject ? option.label : option;

                                        return (
                                            <MenuItem key={optionValue} value={optionValue}>
                                                <Typography noWrap css={styles.dropdownWrap}>
                                                    {t(optionLabel)}
                                                </Typography>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>

                                {error && (
                                    <FormHelperText variant="standard" error>
                                        {helperText}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        );
                    case FieldTypes.Autocomplete: {
                        const dropdownOptions =
                            options?.map((opt) =>
                                typeof opt === 'object' ? opt : { value: opt, label: opt }
                            ) || [];

                        const selectedOption =
                            dropdownOptions.find(
                                (opt) => opt.value === getFirstOption(field.value)
                            ) || null;

                        return (
                            <FormControl css={styles.selectWrapper} fullWidth={fullWidth}>
                                {labelComponent}
                                <Autocomplete
                                    id={id}
                                    options={dropdownOptions}
                                    value={selectedOption}
                                    disabled={disabled}
                                    fullWidth={fullWidth}
                                    getOptionLabel={(option) => t(option.label)}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                    }
                                    onChange={(_, newValue) => {
                                        field.onChange(newValue?.value ?? defaultOption.value);
                                    }}
                                    onOpen={() => {
                                        setTimeout(() => {
                                            const listbox = document.querySelector(
                                                '.MuiAutocomplete-listbox'
                                            );
                                            const selected = listbox?.querySelector(
                                                '[aria-selected="true"]'
                                            ) as HTMLElement;
                                            if (listbox && selected) {
                                                listbox.scrollTop = selected.offsetTop;
                                            }
                                        });
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.value}>
                                            <Typography noWrap css={styles.dropdownWrap}>
                                                {t(option.label)}
                                            </Typography>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            placeholder={placeholder}
                                            error={!!fieldState.error}
                                            required={required}
                                            css={styles.selectText}
                                        />
                                    )}
                                    filterOptions={(opts, state) =>
                                        opts.filter((opt) =>
                                            t(opt.label)
                                                .toLowerCase()
                                                .includes(state.inputValue.toLowerCase())
                                        )
                                    }
                                />
                                {error && (
                                    <FormHelperText variant="standard" error>
                                        {helperText}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        );
                    }
                    case FieldTypes.Phone:
                        return (
                            <PhoneInput
                                {...register(name)}
                                value={field.value || getAsPhoneNumber('')}
                                onChange={field.onChange}
                                label={labelComponent}
                                required={required}
                                error={error}
                                helperText={helperText}
                                disabled={disabled}
                            />
                        );
                    case FieldTypes.DateOfBirth:
                        return (
                            <DateOfBirthInput
                                {...register(name)}
                                value={field.value || ''}
                                label={labelComponent}
                                onChange={field.onChange}
                                required={required}
                                error={error}
                                helperText={helperText}
                                disabled={disabled}
                            />
                        );
                    case FieldTypes.Password:
                        return (
                            <PasswordField
                                {...field}
                                {...register(name)}
                                ref={field.ref}
                                value={field.value}
                                onChange={field.onChange}
                                variant="standard"
                                labelComponent={labelComponent}
                                placeholder={placeholder}
                                fullWidth={fullWidth}
                                required={required}
                                error={error}
                                helperText={helperText}
                                disabled={disabled}
                                id={id}
                            />
                        );
                    case FieldTypes.Checkbox:
                        return (
                            <FormControl error={error}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value}
                                            disabled={disabled}
                                            css={styles.formConfirmationText}
                                            id={id}
                                        />
                                    }
                                    label={<Typography variant="body3">{label}</Typography>}
                                />

                                {error && (
                                    <FormHelperText css={styles.confirmationError}>
                                        {t(helperText)}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        );

                    default:
                        if (isLoginVariant) {
                            return (
                                <TextField
                                    {...field}
                                    {...register(name)}
                                    variant="outlined"
                                    type={type}
                                    placeholder={placeholder}
                                    fullWidth
                                    required
                                    error={error}
                                    disabled={disabled}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    css={css(restStyles)}
                                    InputProps={{ endAdornment }}
                                    FormHelperTextProps={{
                                        sx: {
                                            margin: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                    helperText={
                                        error ? (
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    marginTop: '16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <ErrorIcon fontSize="small" sx={{ mr: 1 }} />
                                                {t(fieldState.error?.message ?? '')}
                                            </Typography>
                                        ) : null
                                    }
                                />
                            );
                        }

                        return (
                            <Box display="flex" flexDirection="column" gap="6px" width="100%">
                                {labelComponent}
                                <TextField
                                    {...field}
                                    {...register(name)}
                                    variant="standard"
                                    type={type}
                                    placeholder={placeholder}
                                    fullWidth={fullWidth}
                                    required={required}
                                    error={error}
                                    helperText={helperText}
                                    id={id}
                                    disabled={disabled}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    css={css(restStyles)}
                                    InputProps={{ endAdornment }}
                                />
                            </Box>
                        );
                }
            }}
        />
    );
};
